import { type FocusEvent, useCallback, useRef } from 'react';

/** Подсказка кнопки, открывающей окно */
export type TriggerTooltip = {
  /** Ставится кнопке: гасит подсказку на возврате фокуса от закрывшегося окна */
  handleFocus: (event: FocusEvent) => void;
  /** Ставится кнопке: движение курсора по ней снимает ожидание возврата фокуса */
  handlePointerMove: () => void;
  /** Открытие и закрытие окна кнопкой */
  handleWindowOpenChange: (isOpen: boolean) => void;
};

/**
 * Подсказка кнопки, которая открывает окно: закрывшись, окно возвращает фокус кнопке, а подсказка
 * всплывает и по фокусу — без этого она появляется сама собой поверх того, ради чего окно и
 * открывали, хотя курсор стоит совсем в другом месте.
 *
 * Гасим единственное событие — тот самый возврат фокуса, отменяя его: отменённое событие подсказка
 * пропускает. Саму подсказку с кнопки не снимаем: она держится на кнопке обёрткой, и снятая —
 * пересобирает кнопку, а вместе с ней и слежение за курсором, из-за чего движение, которым
 * пользователь возвращает подсказку, уходит впустую и подсказку приходится вызывать повторно.
 *
 * Если окно закрылось мимо кнопки (щелчком в стороне), фокус ей не возвращается вовсе — ожидание
 * снимает первое же движение курсора по кнопке, чтобы оно не досталось постороннему фокусу.
 */
export const useTriggerTooltip = (): TriggerTooltip => {
  const isFocusReturnAwaitedRef = useRef(false);

  const handleWindowOpenChange = useCallback((isOpen: boolean) => {
    if (!isOpen) isFocusReturnAwaitedRef.current = true;
  }, []);

  const handleFocus = useCallback((event: FocusEvent) => {
    if (!isFocusReturnAwaitedRef.current) return;
    isFocusReturnAwaitedRef.current = false;
    event.preventDefault();
  }, []);

  const handlePointerMove = useCallback(() => {
    isFocusReturnAwaitedRef.current = false;
  }, []);

  return { handleFocus, handlePointerMove, handleWindowOpenChange };
};
